import { Check, Send } from "lucide-react";
import { Button } from "@flint/ui";
import type { BillTotals, NewBillFormValues, VendorOption } from "./types.js";
import { paiseToInr } from "../../../lib/format.js";

interface NewBillFooterProps {
  values: NewBillFormValues;
  vendor: VendorOption | null;
  totals: BillTotals;
  ready: boolean;
  pending: boolean;
  onCancel: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
}

/**
 * Sticky composer footer — matches `prototype/purchases/bill-new.html .composer-foot`.
 *
 * Left side: ready-state pill + a structured status line derived from the
 * live form (3-way match · GST verification · price-flag count · running
 * bill total). Right side: cancel / draft / submit.
 *
 * Every value here is computed from form state — never hardcoded — so the
 * footer reflects the bill the operator is actually typing, not demo copy.
 */
export function NewBillFooter({
  values,
  vendor,
  totals,
  ready,
  pending,
  onCancel,
  onSaveDraft,
  onSubmit,
}: NewBillFooterProps) {
  const hasGrn = (values.grnNumber ?? "").trim().length > 0;
  const hasPo = (values.poNumber ?? "").trim().length > 0;
  const matchTone = hasGrn && hasPo ? "passed" : hasPo ? "awaiting GRN" : "no PO";

  const gstVerified = !!vendor?.gstin;

  const flagged = (values.lineItems ?? []).filter((line) => !!line.priceFlag);
  const priceFlagCount = flagged.length;

  return (
    <footer className="sticky bottom-0 z-20 border-t border-line bg-paper px-6 py-3 shadow-sm">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}
        >
          {ready ? (
            <>
              <Check className="h-3.5 w-3.5" />
              Ready to submit
            </>
          ) : (
            "Required fields pending"
          )}
        </span>

        {/* Structured status line — every segment derived from form state */}
        <p className="text-sm text-muted">
          <strong className="font-semibold text-ink">3-way match</strong>{" "}
          <StatusSegment tone={hasGrn && hasPo ? "ok" : "muted"}>{matchTone}</StatusSegment>
          {" · "}
          <StatusSegment tone={gstVerified ? "ok" : "muted"}>
            {gstVerified ? "GST verified" : "GSTIN missing"}
          </StatusSegment>
          {priceFlagCount > 0 ? (
            <>
              {" · "}
              <StatusSegment tone="warn">
                {priceFlagCount} price flag{priceFlagCount > 1 ? "s" : ""}
                {totals.totalPaise > 0n ? ` (${describePriceFlags(flagged)})` : ""}
              </StatusSegment>
            </>
          ) : null}
          {" · Total "}
          <span className="font-mono font-semibold text-ink">{paiseToInr(totals.totalPaise)}</span>
        </p>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button type="button" intent="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            intent="ghost"
            className="border border-line"
            onClick={onSaveDraft}
            disabled={pending}
          >
            {pending ? "Saving…" : "Save as draft"}
          </Button>
          <Button type="button" onClick={onSubmit} disabled={pending || !ready}>
            <Send className="h-4 w-4" />
            {pending ? "Submitting…" : "Submit for founder OTP"}
          </Button>
        </div>
      </div>
    </footer>
  );
}

function StatusSegment({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "ok" | "warn" | "muted";
}) {
  if (tone === "ok") return <span className="text-ink-2">{children}</span>;
  if (tone === "warn") return <span className="font-medium text-amber-700">{children}</span>;
  return <span className="text-muted">{children}</span>;
}

/**
 * Best-effort summary of the price-flag delta — pulls the first
 * `₹\d+` token from any `priceFlag` string set on a line item.
 * Falls back to "₹—" when the line carries no numeric token.
 */
function describePriceFlags(lines: ReadonlyArray<{ priceFlag?: string | undefined }>): string {
  const first = lines[0]?.priceFlag ?? "";
  const match = first.match(/₹\s*([\d,.]+)/g);
  if (match && match.length >= 2) return `Δ ${match[1]?.trim() ?? ""}`;
  if (match && match.length === 1) return match[0]?.trim() ?? "";
  return "₹—";
}
